import { ChartDataTable, ChartYGrid, useChartCursor } from './chartParts'
import { formatValue, type FormatOptions, type ValueFormat } from './format'
import { niceMax } from './TrendChart'

export interface DualSeriesPoint {
  /** The x-axis label: "12 Sep". */
  label: string
  /** A longer name for the readout and the table, when the label is abbreviated: "12–18 Sep". */
  detail?: string
  /** The first series' value (drawn as columns); null is no column, never a 0. */
  primary: number | null
  /** The second series' value (drawn as a line); null breaks the line. */
  secondary: number | null
}

export interface DualSeriesChartProps {
  /** The chart's accessible name, and the hidden table's caption. */
  title: string
  points: readonly DualSeriesPoint[]
  /** The legend's, readout's and table's name for each series: say whose figure it is. */
  primaryLabel: string
  secondaryLabel: string
  /** The one unit both series share. Values are in the unit `formatValue` takes (major units for currency). */
  format: ValueFormat
  formatOptions?: FormatOptions
  height?: number
}

const WIDTH = 640
const PAD = { left: 56, right: 12, top: 12, bottom: 28 }
const Y_TICKS = 4
const MAX_X_LABELS = 7
/** A column's share of its slot; the rest is the gap between columns. */
const COLUMN_FILL = 0.6

/** Every `every`th label and the last, dropping a regular one that would crowd the last. */
export function showXLabel(index: number, count: number, every: number): boolean {
  const last = count - 1
  if (index === last) return true
  return index % every === 0 && last - index >= Math.ceil(every / 2)
}

/**
 * Two series that mean different things, drawn over one shared value axis — e.g. ad
 * spend (columns) against the revenue attributed to it (a line), both money. There is
 * deliberately no second y-scale: two scales let the drawing imply whatever crossing
 * point the scales were chosen to give. Both series must therefore share `format`; two
 * measures of different units are two charts.
 *
 * As `TrendChart`, on the same parts (`chartParts`): pointer hover and the arrow keys
 * move a readout announced politely, and the full series is a visually hidden table. Identity is never colour alone: the
 * marks differ (column against line) and the legend, readout and table name each series.
 */
export function DualSeriesChart({
  title,
  points,
  primaryLabel,
  secondaryLabel,
  format,
  formatOptions,
  height = 240,
}: DualSeriesChartProps) {
  const innerWidth = WIDTH - PAD.left - PAD.right
  const slot = innerWidth / Math.max(1, points.length)
  const cursor = useChartCursor(points.length, WIDTH, (position) => Math.floor((position - PAD.left) / slot))
  const { active, readoutId } = cursor

  if (points.length === 0) return null

  const innerHeight = height - PAD.top - PAD.bottom
  const values = points.flatMap((point) => [point.primary, point.secondary]).filter((value): value is number => value !== null)
  const max = niceMax(Math.max(0, ...values))
  const x = (index: number) => PAD.left + slot * (index + 0.5)
  const y = (value: number) => PAD.top + innerHeight - (Math.max(0, value) / max) * innerHeight
  const baseline = PAD.top + innerHeight
  const fmt = (value: number, compact = false) => formatValue(value, format, { ...formatOptions, compact })
  const name = (point: DualSeriesPoint) => point.detail ?? point.label
  const show = (value: number | null) => (value === null ? '—' : fmt(value))

  let linePath = ''
  let pen = false
  points.forEach((point, index) => {
    if (point.secondary === null) {
      pen = false
      return
    }
    linePath += `${pen ? 'L' : 'M'}${x(index).toFixed(1)},${y(point.secondary).toFixed(1)} `
    pen = true
  })

  const columnWidth = Math.max(1, slot * COLUMN_FILL)
  const labelEvery = Math.max(1, Math.ceil(points.length / MAX_X_LABELS))
  const activePoint = active === null ? null : points[active]

  return (
    <figure className="ci-dual">
      <div className="ci-trend__plot" aria-label={`${title}. Use the arrow keys to read values.`} {...cursor.plotProps}>
        <svg
          viewBox={`0 0 ${WIDTH} ${height}`}
          className="ci-trend__svg"
          aria-hidden="true"
          focusable="false"
          {...cursor.svgProps}
        >
          <ChartYGrid max={max} ticks={Y_TICKS} left={PAD.left} right={WIDTH - PAD.right} y={y} label={(value) => fmt(value, true)} />
          {points.map((point, index) =>
            showXLabel(index, points.length, labelEvery) ? (
              <text key={`x-${point.label}-${index}`} className="ci-trend__x" x={x(index)} y={height - 8} textAnchor="middle">
                {point.label}
              </text>
            ) : null,
          )}
          {active !== null ? (
            <rect className="ci-dual__hover" x={PAD.left + slot * active} y={PAD.top} width={slot} height={innerHeight} />
          ) : null}
          {points.map((point, index) =>
            point.primary !== null && point.primary > 0 ? (
              <rect
                key={`c-${point.label}-${index}`}
                className="ci-dual__column"
                data-testid="dual-column"
                x={x(index) - columnWidth / 2}
                y={y(point.primary)}
                width={columnWidth}
                height={Math.max(0, baseline - y(point.primary))}
                rx={Math.min(2, columnWidth / 2)}
              />
            ) : null,
          )}
          {linePath ? <path d={linePath.trim()} className="ci-dual__line" data-testid="dual-line" /> : null}
          {activePoint && active !== null && activePoint.secondary !== null ? (
            <circle className="ci-dual__marker" cx={x(active)} cy={y(activePoint.secondary)} r={4} />
          ) : null}
        </svg>
        <div
          id={readoutId}
          className="ci-trend__readout"
          aria-live="polite"
          data-visible={activePoint ? 'true' : 'false'}
          style={active !== null ? { left: `${(x(active) / WIDTH) * 100}%` } : undefined}
        >
          {activePoint ? (
            <>
              <strong>{name(activePoint)}</strong>
              <span>
                {primaryLabel}: {show(activePoint.primary)}
              </span>
              <span>
                {secondaryLabel}: {show(activePoint.secondary)}
              </span>
            </>
          ) : null}
        </div>
      </div>
      <figcaption className="ci-trend__legend">
        <span className="ci-dual__key" data-series="primary">
          {primaryLabel}
        </span>
        <span className="ci-dual__key" data-series="secondary">
          {secondaryLabel}
        </span>
      </figcaption>
      <ChartDataTable
        title={title}
        columns={['Point', primaryLabel, secondaryLabel]}
        rows={points.map((point, index) => ({
          key: `${point.label}-${index}`,
          name: name(point),
          cells: [show(point.primary), show(point.secondary)],
        }))}
      />
    </figure>
  )
}
